/**
 * ScriptMirror — a read-only render of the live script with the current reading
 * block highlighted (the ▶ marker), so the producer/admin sees exactly where the
 * talent is up to. When `onJump` is supplied, every spoken line becomes clickable:
 * clicking sends the presenter's reading position to that block (re-align the talent
 * if they lose their place). Not mirror-flipped — the flip is only for the glass.
 */
import { useEffect, useRef } from 'react';
import type { Project } from '@goanna-im/shared';

export function ScriptMirror({
  project,
  anchorBlockId,
  onJump,
  maxHeight = '48vh',
  fill = false,
  autoScroll = true,
}: {
  project: Project;
  anchorBlockId: string;
  /** When provided, lines are clickable and call this with the target block id. */
  onJump?: (blockId: string) => void;
  maxHeight?: string;
  /** Grow to fill the parent (flex column) instead of capping at maxHeight. */
  fill?: boolean;
  /** Keep the current reading line in view as it moves. */
  autoScroll?: boolean;
}) {
  const hereRef = useRef<HTMLParagraphElement>(null);

  // Follow the reading position — scroll the highlighted line into view.
  useEffect(() => {
    if (autoScroll) hereRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [anchorBlockId, autoScroll]);

  return (
    <div
      className="card"
      style={
        fill
          ? { flex: 1, minHeight: '340px', overflow: 'auto', padding: '0.75rem 1rem', lineHeight: 1.5 }
          : { maxHeight, overflow: 'auto', padding: '0.75rem 1rem', lineHeight: 1.5 }
      }
    >
      <h4 style={{ margin: '0 0 0.5rem' }}>{project.title}</h4>
      {project.sections.map((section) => (
        <div key={section.id} style={{ marginBottom: '0.75rem' }}>
          {section.heading && (
            <div
              className="muted"
              style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}
            >
              {section.heading}
            </div>
          )}
          {section.blocks.map((b) => {
            const here = b.id === anchorBlockId;
            const direction = b.type === 'direction' || b.type === 'marker';
            const clickable = !!onJump && !direction;
            return (
              <p
                key={b.id}
                ref={here ? hereRef : undefined}
                onClick={clickable ? () => onJump!(b.id) : undefined}
                title={clickable ? 'Send the presenter here' : undefined}
                style={{
                  margin: '0.25rem 0',
                  padding: here ? '0.15rem 0.4rem' : clickable ? '0.15rem 0.4rem' : undefined,
                  borderLeft: here ? '3px solid var(--accent, #22d3ee)' : '3px solid transparent',
                  borderRadius: clickable ? '4px' : undefined,
                  background: here ? 'var(--accent-dim, rgba(34,211,238,0.12))' : undefined,
                  fontStyle: direction ? 'italic' : undefined,
                  color: direction ? 'var(--muted)' : undefined,
                  cursor: clickable ? 'pointer' : undefined,
                }}
                className={clickable ? 'script-mirror-line' : undefined}
              >
                {here && (
                  <span aria-hidden style={{ marginRight: '0.4rem' }}>
                    ▶
                  </span>
                )}
                {b.text || (direction ? `[${b.type}]` : '')}
              </p>
            );
          })}
        </div>
      ))}
    </div>
  );
}
